import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { PlayerCard } from "@/components/PlayerCard";
import { SaveSearchButton } from "@/components/SaveSearchButton";
import { SPORTS } from "@/constants/sports";
import { DEFAULT_FILTERS, FilterPanel } from "@/components/FilterPanel";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { AGE_RANGE, HEIGHT_RANGE } from "@/constants/sports";
import { api } from "@/lib/api";

const toParams = (q, f) => {
  const p = {};
  if (q.trim()) p.q = q.trim();
  ["sport", "position", "team", "class_year", "division", "position_group"].forEach((k) => f[k] !== "all" && (p[k] = f[k]));
  if (f.age[0] !== AGE_RANGE[0] || f.age[1] !== AGE_RANGE[1]) [p.min_age, p.max_age] = f.age;
  if (f.height[0] !== HEIGHT_RANGE[0] || f.height[1] !== HEIGHT_RANGE[1]) [p.min_height, p.max_height] = f.height;
  if (f.attrs.length) p.attrs = f.attrs.join(",");
  return p;
};

const URL_KEYS = ["sport", "position", "team", "class_year", "division", "position_group"];
const fromUrl = (sp) => ({
  ...DEFAULT_FILTERS,
  ...Object.fromEntries(URL_KEYS.filter((k) => sp.get(k)).map((k) => [k, sp.get(k)])),
  attrs: (sp.get("attrs") || "").split(",").filter(Boolean),
});

export default function SearchPage() {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [filters, setFilters] = useState(() => fromUrl(sp));
  const [meta, setMeta] = useState({});
  const [data, setData] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => { api.get("/players/meta").then((r) => setMeta(r.data)); }, []);
  useEffect(() => {
    const p = { ...(q.trim() ? { q: q.trim() } : {}), ...Object.fromEntries(URL_KEYS.filter((k) => filters[k] !== "all").map((k) => [k, filters[k]])),
      ...(filters.attrs.length ? { attrs: filters.attrs.join(",") } : {}) };
    setSp(p, { replace: true });
    const t = setTimeout(() => api.get("/players", { params: toParams(q, filters) }).then((r) => setData(r.data)), 250);
    return () => clearTimeout(t);
  }, [q, filters, setSp]);
  const attrTags = meta.attributes?.[filters.sport]?.tags || [];
  const searchParams = { ...(q.trim() ? { q: q.trim() } : {}), ...Object.fromEntries(URL_KEYS.filter((k) => filters[k] !== "all").map((k) => [k, filters[k]])),
    ...(filters.attrs.length ? { attrs: filters.attrs.join(",") } : {}) };
  const suggestedName = [SPORTS[filters.sport]?.label, ...filters.attrs.map((k) => attrTags.find((t) => t.key === k)?.label || k),
    filters.position !== "all" && filters.position, filters.team !== "all" && filters.team, q.trim()].filter(Boolean).join(" · ");
  const removeAttr = (k) => setFilters((f) => ({ ...f, attrs: f.attrs.filter((x) => x !== k) }));

  const loadMore = async () => {
    setLoadingMore(true);
    const r = await api.get("/players", { params: { ...toParams(q, filters), skip: data.items.length } });
    setData((d) => ({ ...d, items: [...d.items, ...r.data.items] }));
    setLoadingMore(false);
  };

  return (
    <div className="min-h-screen bg-[#0B0F19]">
      <AppHeader />
      <main className="mx-auto max-w-[1400px] px-4 pb-24 pt-10 sm:px-8">
        <div className="rise mb-10">
          <p className="eyebrow !text-[#FF2D78]">Player database</p>
          <h1 className="mt-3 font-display text-4xl font-black uppercase tracking-tight sm:text-5xl lg:text-6xl">Find her.</h1>
        </div>
        <div className="mb-10 flex gap-3">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
            <input data-testid="player-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players by name"
              className="h-14 w-full rounded-xl border border-slate-800 bg-[#131927] pl-14 pr-4 text-base text-white outline-none transition-colors duration-200 placeholder:text-slate-500 focus:border-[#FF2D78]" />
          </div>
          <SaveSearchButton params={searchParams} suggestedName={suggestedName} />
          <Sheet>
            <SheetTrigger asChild>
              <button data-testid="mobile-filters-button" className="flex h-14 items-center gap-2 rounded-xl border border-slate-800 bg-[#131927] px-4 text-sm lg:hidden">
                <SlidersHorizontal className="h-4 w-4" /> Filters
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[320px] overflow-y-auto border-slate-800 bg-[#0B0F19]">
              <SheetHeader><SheetTitle className="font-display text-2xl uppercase">Filters</SheetTitle></SheetHeader>
              <div className="mt-8"><FilterPanel filters={filters} setFilters={setFilters} meta={meta} /></div>
            </SheetContent>
          </Sheet>
        </div>
        <div className="grid gap-10 lg:grid-cols-[260px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-24 rounded-xl border border-slate-800/80 bg-[#131927]/60 p-6">
              <FilterPanel filters={filters} setFilters={setFilters} meta={meta} />
            </div>
          </aside>
          <section>
            <p data-testid="results-count" className="eyebrow mb-5">
              {data ? `${data.total} player${data.total === 1 ? "" : "s"}` : "Loading"}
            </p>
            <div className="grid grid-cols-1 gap-5 min-[480px]:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {!data && [...Array(6)].map((_, i) => <Skeleton key={i} className="aspect-[3/4] rounded-xl bg-[#131927]" />)}
              {data?.items.map((p, i) => <PlayerCard key={p.id} p={p} index={i} matchKeys={filters.attrs} />)}
            </div>
            {data && data.items.length < data.total && (
              <div className="mt-10 flex justify-center">
                <button data-testid="load-more-button" onClick={loadMore} disabled={loadingMore}
                  className="rounded-full border border-slate-700 px-8 py-3 font-data text-xs uppercase tracking-widest text-slate-200 transition-colors duration-200 hover:border-[#FF2D78] disabled:opacity-50">
                  {loadingMore ? "Loading" : `Load more (${data.total - data.items.length} left)`}
                </button>
              </div>
            )}
            {data && data.total === 0 && (
              <div data-testid="no-results" className="rounded-xl border border-dashed border-slate-800 p-16 text-center">
                <p className="font-display text-3xl font-extrabold uppercase">No players found</p>
                {filters.attrs.length > 0 ? (
                  <>
                    <p className="mt-2 text-sm text-slate-400">No player has all of these attributes. Try removing one:</p>
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      {filters.attrs.map((k) => (
                        <button key={k} data-testid={`no-results-remove-attribute-${k}`} onClick={() => removeAttr(k)}
                          className="rounded-full border border-[#FF2D78]/50 px-3 py-1 text-xs text-white transition-colors duration-200 hover:bg-[#FF2D78]/20">
                          {attrTags.find((t) => t.key === k)?.label || k} ×
                        </button>
                      ))}
                    </div>
                  </>
                ) : <p className="mt-2 text-sm text-slate-400">Try a different name or loosen your filters.</p>}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
